/**
 * environmentParts — the rules for a MULTIPART environment (ENG-1132).
 *
 * An environment can be several splats rather than one: the furniture as its
 * own file, separate from the walls. Stored on the environment row as a
 * name-keyed map, exactly like `anchors`:
 *
 *   "parts": {
 *     "sofa": {"file": "parts/sofa.spz", "url": "<the serve URL the API returned>",
 *              "label": "the sofa", "tags": ["furniture"],
 *              "px": 0, "py": 0, "pz": 0, "rx": 0, "ry": 0, "rz": 0, "scale": 1}
 *   }
 *
 * ── Why this exists when asset tags already do ──────────────────────────────
 * A tag on the environment ROW classifies the whole room and implies no
 * location, so "walk to the sofa" has nothing to resolve. The sofa acquires a
 * position only when it is its own splat with its own transform. The part
 * carries the three things a row-level tag cannot: a file, a transform, and its
 * own tags.
 *
 * ── The transform COMPOSES UNDER the environment's pose ─────────────────────
 * World = envPose ∘ partPose, which is why `partMount` returns a LOCAL
 * transform and the caller puts every part inside one group carrying the
 * environment's pose. The row's `default_pose` is the registration of the
 * capture into world space (Startup_Level: ry 3.14, scale 0.7); if a part
 * carried an absolute world transform, re-registering the room would mean
 * rewriting every part.
 *
 * ── `url` comes from the server, always ─────────────────────────────────────
 * Nothing here builds one. The API resolves each part's folder-relative `file`
 * to a serve URL and this module reads it verbatim — the ENG-999 rule, and the
 * reason a part whose `url` is missing is DROPPED AND NAMED rather than
 * pointed at a guessed path.
 *
 * ── Mirrors, not local opinions ─────────────────────────────────────────────
 * PART_NAME_RE, MAX_PARTS and MAX_PART_LABEL are copied from
 * avatar-asset-manager's environment_parts.go. The repos cannot import each
 * other, so drift means the UI accepts a part the PATCH rejects and the user's
 * only evidence is a save that failed for something that looked fine. Change
 * one, change both.
 *
 * Pure: no THREE, no DOM, no React, no network — it runs under `node --test`.
 */

import { normalizePose } from './envPose.js'
import { readTags } from './assetTags.js'

/** Mirrors avatar-asset-manager's partNameRe (and the anchor name rule). */
export const PART_NAME_RE = /^[a-z][a-z0-9_]{0,31}$/

/** Mirrors MaxPartsPerEnvironment. Every part is a splat the browser downloads,
 *  parses and renders, so a room split into hundreds is a load-time problem
 *  long before it is a storage one. */
export const MAX_PARTS = 64

/** Mirrors MaxPartLabel. */
export const MAX_PART_LABEL = 120

/** The name the server gives the row's own file when it seeds it as a part. */
export const BASE_PART_NAME = 'base'

/**
 * Read the parts off an environment asset, in any shape it reaches the UI in.
 *
 * A malformed entry is DROPPED AND NAMED rather than silently skipped, the same
 * rule readAnchors follows: the server validates everything it stores, so the
 * only way to get one is a hand-edited column — and a part silently missing
 * from a room looks exactly like a room that was authored that way.
 *
 * @returns {{parts: Record<string, object>, dropped: string[]}}
 */
export function readParts(asset) {
  const raw = asset?.parts ?? asset?.raw?.parts ?? asset?.raw?.Parts ?? null
  let parsed = raw
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed) } catch { parsed = null }
  }
  if (parsed == null) return { parts: {}, dropped: [] }
  if (typeof parsed !== 'object' || Array.isArray(parsed)) {
    console.error('[environmentParts] parts is not an object, ignoring:', parsed)
    return { parts: {}, dropped: [] }
  }
  const parts = {}
  const dropped = []
  for (const [name, p] of Object.entries(parsed)) {
    if (!PART_NAME_RE.test(name) || !p || typeof p !== 'object' || Array.isArray(p)) {
      dropped.push(name)
      continue
    }
    // No url means the server did not resolve one, which can only be a row this
    // client cannot render. Deriving one from `file` is exactly what ENG-999
    // forbids, and it would 404 indistinguishably from "not uploaded yet".
    if (typeof p.url !== 'string' || p.url === '') {
      dropped.push(name)
      continue
    }
    parts[name] = {
      file: typeof p.file === 'string' ? p.file : '',
      url: p.url,
      label: typeof p.label === 'string' ? p.label : '',
      tags: readTags(p),
      format: typeof p.format === 'string' ? p.format : '',
      byteSize: Number.isFinite(Number(p.byteSize)) ? Number(p.byteSize) : 0,
      pose: normalizePose(p),
    }
  }
  if (dropped.length) {
    console.error('[environmentParts] dropped unreadable parts:', dropped.join(', '))
  }
  return { parts, dropped }
}

/**
 * The parts as a stably-ordered list, for rendering and for the inspector.
 *
 * Sorted by name for the same reason anchorList is: the server re-marshals the
 * map on every write, so insertion order is not a property anyone can rely on,
 * and a list that reshuffles itself after each save reads as data loss.
 *
 * Accepts the MAP form (what readParts returns, what the API stores) or the
 * LIST form. Both exist for one concrete reason: liveTuneStore's deepMerge
 * merges plain objects and REPLACES arrays, so an environment's parts are held
 * there as a list. Stored as a map, switching from a four-part room to a
 * one-part room would merge the two and leave the old room's furniture standing
 * inside the new one — populated, plausible, and completely wrong.
 */
export function partList(parts) {
  if (Array.isArray(parts)) {
    return [...parts].sort((a, b) => String(a?.name).localeCompare(String(b?.name)))
  }
  return Object.keys(parts ?? {})
    .sort((a, b) => a.localeCompare(b))
    .map(name => ({ name, ...parts[name] }))
}

/** The map form, from either form. The inverse of partList. */
export function partMap(parts) {
  if (!Array.isArray(parts)) return parts ?? {}
  const out = {}
  for (const p of parts) {
    if (p?.name) out[p.name] = { ...p, name: undefined }
  }
  return out
}

/**
 * What to MOUNT for an environment: one entry per splat, each with the LOCAL
 * transform to apply inside the environment's own posed group.
 *
 * `baseUrl` is the row's `fileUrl` — what a zero-part environment renders, and
 * what identifies the part standing for the row's own file once parts exist.
 *
 * Two rules here are the whole contract with the renderer:
 *
 *  - A NON-EMPTY parts map REPLACES the base URL. The server seeds the row's
 *    own file as a part on the first add precisely so this is safe; rendering
 *    the base in ADDITION would draw the walls twice, at double opacity, which
 *    reads as a badly exposed scan rather than as a bug.
 *  - Exactly one mount is the FLOOR SOURCE, and it is the part whose url is the
 *    base file — the room scan, the thing with a floor in it. Estimating the
 *    floor from a prop puts the character's feet on top of the sofa. With no
 *    part matching (a row whose base was replaced), the first part is used and
 *    it says so, because a silently unestimated floor buries the avatar.
 *
 * @returns {{mounts: Array<{key,name,url,position,rotation,scale,isFloorSource}>, multipart: boolean}}
 */
export function partMounts(parts, baseUrl) {
  const list = partList(parts)
  if (list.length === 0) {
    if (!baseUrl) return { mounts: [], multipart: false }
    return {
      mounts: [{
        key: baseUrl,
        name: BASE_PART_NAME,
        url: baseUrl,
        position: [0, 0, 0],
        rotation: [0, 0, 0],
        scale: 1,
        isFloorSource: true,
      }],
      multipart: false,
    }
  }

  const stripQuery = (u) => (u || '').split('?')[0]
  const base = stripQuery(baseUrl)
  let floorIdx = list.findIndex(p => stripQuery(p.url) === base && base !== '')
  if (floorIdx < 0) {
    floorIdx = 0
    console.warn(
      `[environmentParts] no part names the environment's own file (${baseUrl || 'none'}); ` +
      `estimating the floor from "${list[0].name}" instead`,
    )
  }

  return {
    mounts: list.map((p, i) => ({
      // Keyed by URL, not by name. The base part is mounted under its URL
      // BEFORE the row's parts are known (a reload restores splatUrl alone and
      // Scene resolves the parts a moment later), so keying by name would
      // remount it under a new key the instant they arrive and re-download a
      // several-hundred-megabyte room scan.
      key: p.url,
      name: p.name,
      url: p.url,
      position: [p.pose.px, p.pose.py, p.pose.pz],
      rotation: [p.pose.rx, p.pose.ry, p.pose.rz],
      scale: p.pose.scale,
      isFloorSource: i === floorIdx,
    })),
    multipart: true,
  }
}

/** Every tag on any part of this environment, deduped and sorted — what the
 *  content browser can offer as a filter and what a character can be asked
 *  about ("the furniture"). */
export function partTags(parts) {
  const seen = new Set()
  for (const p of partList(parts)) {
    for (const t of p.tags ?? []) seen.add(t)
  }
  return [...seen].sort()
}

/** The parts carrying `tag`, as a name list. */
export function partsWithTag(parts, tag) {
  const want = String(tag ?? '').trim().toLowerCase()
  if (!want) return []
  return partList(parts).filter(p => (p.tags ?? []).includes(want)).map(p => p.name)
}

/** Why `name` cannot be used, or null when it can. */
export function partNameError(name, existing = {}, self = null) {
  if (typeof name !== 'string' || name === '') return 'name is required'
  if (!PART_NAME_RE.test(name)) {
    return 'lowercase letters, digits and _ only, starting with a letter, max 32'
  }
  if (name !== self && Object.prototype.hasOwnProperty.call(existing, name)) {
    return `"${name}" already exists`
  }
  return null
}

/** Why `label` cannot be used, or null when it can. Empty is allowed. */
export function partLabelError(label) {
  if (label == null || label === '') return null
  if (typeof label !== 'string') return 'label must be text'
  if ([...label.trim()].length > MAX_PART_LABEL) return `at most ${MAX_PART_LABEL} characters`
  if (/[\n\r]/.test(label)) return 'must be a single line'
  return null
}

/**
 * The value to PATCH as `parts`.
 *
 * Drops `url`, `format` and `byteSize` — all three are the SERVER's, derived
 * per response or known only at upload. Sending a `url` back would be the
 * client asserting where bytes live, which is the one thing it must never do;
 * the server ignores it, and a client that believes it is being honoured will
 * eventually act on that belief.
 *
 * `{}` is a legal body and CLEARS the parts, which is why the caller must mean
 * it (the server treats an omitted key as a no-op and an explicit null as an
 * error). Clearing does NOT delete the files — the environment simply renders
 * from its own file again.
 */
export function serializeParts(parts) {
  const out = {}
  for (const [name, p] of Object.entries(partMap(parts))) {
    if (!PART_NAME_RE.test(name)) throw new Error(`invalid part name "${name}"`)
    if (!p?.file) throw new Error(`part "${name}" has no file`)
    const entry = { file: p.file }
    const label = (p.label ?? '').trim()
    if (label) entry.label = label
    const tags = readTags({ tags: p.tags ?? [] })
    if (tags.length) entry.tags = tags
    const pose = normalizePose(p.pose ?? {})
    for (const k of ['px', 'py', 'pz', 'rx', 'ry', 'rz']) {
      if (pose[k] !== 0) entry[k] = pose[k]
    }
    if (pose.scale !== 1) entry.scale = pose.scale
    out[name] = entry
  }
  return out
}

/** `parts` with `name` removed (a no-op for a name that is not there). */
export function withoutPart(parts, name) {
  if (!Object.prototype.hasOwnProperty.call(parts ?? {}, name)) return parts ?? {}
  const next = { ...parts }
  delete next[name]
  return next
}

/** `parts` with `name`'s field replaced. Returns a NEW map. */
export function withPartField(parts, name, field, value) {
  const cur = parts?.[name]
  if (!cur) throw new Error(`cannot edit "${name}": no such part`)
  return { ...parts, [name]: { ...cur, [field]: value } }
}

/** Value equality over two part maps, for dirty tracking. */
export function partsEqual(a, b) {
  const ka = Object.keys(a ?? {})
  const kb = Object.keys(b ?? {})
  if (ka.length !== kb.length) return false
  for (const k of ka) {
    const x = a[k], y = b?.[k]
    if (!y) return false
    if (x.file !== y.file) return false
    if ((x.label ?? '') !== (y.label ?? '')) return false
    if ((x.tags ?? []).join(',') !== (y.tags ?? []).join(',')) return false
    const px = normalizePose(x.pose ?? {}), py = normalizePose(y.pose ?? {})
    for (const c of ['px', 'py', 'pz', 'rx', 'ry', 'rz', 'scale']) {
      if (Math.abs(px[c] - py[c]) > 1e-6) return false
    }
  }
  return true
}

/**
 * A part's position in the environment's own space, for minting an anchor from
 * it ("walk to the sofa").
 *
 * This is the part's ORIGIN, not the centre of its geometry: the bounding box
 * is only knowable once the splat is loaded, and this module is pure. A caller
 * with a loaded mesh should prefer the box centre and say so. The origin is
 * still the right default — a prop exported on its own is authored about its
 * own origin — but it is a starting point a human adjusts, which is why
 * minting an anchor from a part is an explicit action rather than something
 * that happens by itself. See environmentAnchors.js for why an anchor needs a
 * human-chosen standing spot and a label.
 */
export function partOrigin(part) {
  const pose = normalizePose(part?.pose ?? {})
  return [pose.px, pose.py, pose.pz]
}
